import { Link, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"
import { Button, buttonVariants } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { cn } from "../../lib/cn"
import { formatCnpj, sanitizeDocumentInput } from "../../lib/brazilianDocument"

type CompanyFormData = {
  id?: number
  name: string
  cnpj: string
  omie_key: string
  omie_secret: string
  omie_email_copy: string
  modo_emissao: "web" | "txt" | "web_e_txt"
}

type CompanyFormProps = {
  initialValues: CompanyFormData
  submitUrl: string
  submitLabel: string
  method: "post" | "patch"
  errors?: Record<string, string[]>
}

function ErrorText({ value }: { value?: string[] }) {
  if (!value?.length) return null
  return <p className="text-xs text-rose-400">{value[0]}</p>
}

export function CompanyForm({ initialValues, submitUrl, submitLabel, method, errors }: CompanyFormProps) {
  const form = useForm({
    company: {
      name: initialValues.name ?? "",
      cnpj: sanitizeDocumentInput(initialValues.cnpj ?? ""),
      omie_key: initialValues.omie_key ?? "",
      omie_secret: initialValues.omie_secret ?? "",
      omie_email_copy: initialValues.omie_email_copy ?? "",
      modo_emissao: initialValues.modo_emissao ?? "web_e_txt"
    }
  })

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (method === "patch") {
      form.patch(submitUrl)
      return
    }
    form.post(submitUrl)
  }

  return (
    <form onSubmit={submit} className="space-y-6 rounded-xl border border-slate-800 bg-slate-900 p-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="name">Nome</Label>
          <Input
            id="name"
            value={form.data.company.name}
            onChange={(e) => form.setData("company", { ...form.data.company, name: e.target.value })}
          />
          <ErrorText value={errors?.name} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="cnpj">CNPJ</Label>
          <Input
            id="cnpj"
            placeholder="00.000.000/0000-00"
            value={formatCnpj(form.data.company.cnpj)}
            onChange={(e) => form.setData("company", { ...form.data.company, cnpj: sanitizeDocumentInput(e.target.value) })}
          />
          <ErrorText value={errors?.cnpj} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="modo_emissao">Modo de emissão</Label>
          <select
            id="modo_emissao"
            value={form.data.company.modo_emissao}
            onChange={(e) =>
              form.setData("company", {
                ...form.data.company,
                modo_emissao: e.target.value as CompanyFormData["modo_emissao"]
              })
            }
            className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
          >
            <option value="web">Somente Web</option>
            <option value="txt">Somente TXT</option>
            <option value="web_e_txt">Web + TXT</option>
          </select>
          <ErrorText value={errors?.modo_emissao} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="omie_key">Omie Key</Label>
          <Input
            id="omie_key"
            value={form.data.company.omie_key}
            onChange={(e) => form.setData("company", { ...form.data.company, omie_key: e.target.value })}
          />
          <ErrorText value={errors?.omie_key} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="omie_secret">Omie Secret</Label>
          <Input
            id="omie_secret"
            value={form.data.company.omie_secret}
            onChange={(e) => form.setData("company", { ...form.data.company, omie_secret: e.target.value })}
          />
          <ErrorText value={errors?.omie_secret} />
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="omie_email_copy">E-mail padrão de envio da OS</Label>
          <Input
            id="omie_email_copy"
            type="email"
            placeholder="financeiro@empresa.com.br"
            value={form.data.company.omie_email_copy}
            onChange={(e) => form.setData("company", { ...form.data.company, omie_email_copy: e.target.value })}
          />
          <ErrorText value={errors?.omie_email_copy} />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        <Link href="/empresas" className={cn(buttonVariants({ variant: "outline" }), "border-slate-700 bg-transparent")}>
          Cancelar
        </Link>
        <Button type="submit" disabled={form.processing}>
          {form.processing ? "Salvando..." : submitLabel}
        </Button>
      </div>
    </form>
  )
}
